"use client";

import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { ArrowRightIcon, BookmarkIcon, MapPinIcon } from "lucide-react";
import { formatPrice } from "@/app/utils/helpers/format";
import { Button } from "@/app/components/ui/button";
import { cn } from "@/lib/utils";
import { Badge } from "@/app/components/ui/badge";
import { CheckIcon } from "lucide-react";
import {
  type EnvkvT,
  renderEnvkvConsumption,
} from "../../gebrauchtwagen-old/lib/vehicles-formatter";
import { useIsMobile } from "@/app/hooks/use-mobile";
import { useBookmark } from "@/app/hooks/use-bookmark";
import { useBrand } from "@/app/components/theme/brand-theme-boundary";
import { buildBrandPath } from "@/app/config/brand-registry";

type VehicleCardProps = {
  vehicle: {
    id: string;
    slug: string | null;
    make: string | null;
    model: string | null;
    vehicleid: number | null;
    body: string | null;
    fuel: string | null;
    transmission: string | null;
    color: string | null;
    power_kw: number | null;
    localcode: string | null;
    power_hp: number | null;
    customerprice: number | null;
    kilometers: number | null;
    envkv: EnvkvT | null;
    mwst: boolean | null;
    sellingdealer_city: string | null;
    sellingdealer_firm: string | null;
    sellingdealer_street: string | null;
    sellingdealer_dealercountry: string | null;
    sellingdealer_postalcode: string | null;
    thumbnail: string | null;
    images_s: string[] | null;
  };
  isFirst?: boolean;
};

function VehicleGridItem({
  label,
  value,
  className,
}: {
  label: string;
  value: string | number | null | undefined;
  className?: string;
}) {
  return (
    <div className={cn("flex flex-col capitalize", className)}>
      <div className="text-secondary-foreground text-sm font-normal">
        {label}
      </div>
      <div className="font-semibold">{value ?? "—"}</div>
    </div>
  );
}

function toHp(kw: number | null | undefined): string {
  if (kw === null || kw === undefined || Number.isNaN(kw)) return "—";
  return `${Math.round(kw * 1.35962)} PS`;
}

function formatKm(km: number | null | undefined): string {
  if (km === null || km === undefined) return "—";
  return `${km.toLocaleString("de-DE")} km`;
}

export default function VehicleCard({
  vehicle,
  isFirst = false,
}: VehicleCardProps) {
  const { brandKey } = useBrand();
  const isMobile = useIsMobile();
  const { toggleVehicleBookmark, isVehicleBookmarked } = useBookmark();
  const [isMounted, setIsMounted] = useState(false);
  const imageSrc = vehicle.thumbnail ?? "/nothing-found.webp";
  const otherImageSrcs = vehicle.images_s ?? [];
  const href = vehicle.slug
    ? buildBrandPath(brandKey, `/gebrauchtwagen/${vehicle.slug}`)
    : "#";
  const bookmarkId =
    typeof vehicle.vehicleid === "number" ? vehicle.vehicleid : null;
  const isBookmarked =
    isMounted && bookmarkId !== null ? isVehicleBookmarked(bookmarkId) : false;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const handleBookmarkToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (bookmarkId === null) return;
    toggleVehicleBookmark(bookmarkId);
  };

  const mwstText =
    vehicle.mwst === null || vehicle.mwst === undefined
      ? ""
      : vehicle.mwst
        ? "inkl. MwSt. (ausweisbar)"
        : "inkl. MwSt. (nicht ausweisbar)";

  return (
    <Link
      href={href}
      prefetch={false}
      className="relative flex min-h-[380px] w-full overflow-hidden rounded-xl border border-gray-100 bg-white px-0 py-0 hover:shadow-sm"
      suppressHydrationWarning
    >
      {!isMobile && (
        <Button
          type="button"
          className={cn(
            "absolute top-4 right-4 z-10 flex h-10 w-10 cursor-pointer justify-center rounded-lg border-0 px-0 py-0 [&_svg]:size-5",
            isBookmarked
              ? "bg-primary text-primary-foreground"
              : "bg-primary/80 text-primary-foreground hover:bg-primary"
          )}
          onClick={handleBookmarkToggle}
          aria-label={isBookmarked ? "Fahrzeug entfernen" : "Fahrzeug merken"}
        >
          <BookmarkIcon
            className="size-6"
            strokeWidth={1.5}
            fill={isBookmarked ? "currentColor" : "none"}
          />
        </Button>
      )}
      <div className="grid w-full grid-cols-1 md:grid-cols-[300px_1fr] xl:grid-cols-[380px_1fr]">
        <div className="relative h-full w-full">
          <div className="relative aspect-[16/9] h-full w-full md:aspect-auto">
            <div
              className={`absolute inset-0 grid h-full w-full gap-0.5 md:flex md:flex-col ${otherImageSrcs.length > 1 ? "grid-cols-[3fr_1fr]" : "md:grid-cols-[1fr]"}`}
            >
              <div className="relative h-full w-full overflow-hidden md:max-h-[260px] xl:max-h-full">
                <Image
                  fill
                  sizes="(min-width: 1280px) 380px, (min-width: 768px) 300px, 100vw"
                  src={imageSrc}
                  alt={`${vehicle.make} ${vehicle.model}`}
                  className="object-cover"
                  fetchPriority={isFirst ? "high" : "auto"}
                  loading={isFirst ? "eager" : "lazy"}
                />
              </div>
              {otherImageSrcs.length > 1 ? (
                <div className="grid w-full grid-rows-3 gap-0.5 md:grid-cols-3 md:grid-rows-1">
                  {otherImageSrcs.map((src, idx) => (
                    <div key={idx} className="relative w-full overflow-hidden">
                      <div className="relative aspect-[16/10] w-full max-md:h-full">
                        <Image
                          fill
                          sizes="125px"
                          quality={50}
                          src={src}
                          alt={`${vehicle.make} ${vehicle.model}`}
                          className="object-cover"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col p-4 sm:p-6 xl:p-8">
          <h3 className="text-xl font-semibold">
            {vehicle.make} {vehicle.model}
          </h3>
          <div className="mt-3 flex grow flex-col max-xl:gap-4 xl:grid xl:grid-cols-2">
            <div className="grid w-full grid-cols-2 gap-3 xl:flex xl:flex-col">
              <div className="hidden flex-col flex-wrap gap-2 sm:flex lg:flex-row">
                <Badge className="text-muted-foreground bg-muted">
                  <CheckIcon /> Sofort verfügbar
                </Badge>
                <Badge className="text-muted-foreground bg-muted">
                  <MapPinIcon />
                  {vehicle.sellingdealer_city}
                </Badge>
              </div>
              <VehicleGridItem
                label="Kilometerstand"
                value={formatKm(vehicle.kilometers)}
              />
              <VehicleGridItem
                label="Leistung"
                value={toHp(vehicle.power_kw)}
              />
              <VehicleGridItem label="Kraftstoff" value={vehicle.fuel} />
              <VehicleGridItem label="Farbe" value={vehicle.color} />
            </div>
            <div className="flex w-full flex-col gap-2">
              <div className="flex w-full flex-col gap-3">
                <VehicleGridItem
                  label="Interne Fahrzeugnummer"
                  value={vehicle.localcode}
                />
                <div className="text-sm">
                  {renderEnvkvConsumption(vehicle.envkv)}
                </div>
              </div>

              <div className="flex items-start gap-2 text-sm font-normal max-lg:py-2 md:mt-2">
                <span className="mt-0.5 size-2 shrink-0 rounded-full">
                  <MapPinIcon size={12} />
                </span>
                {`${vehicle.sellingdealer_firm || ""}, ${vehicle.sellingdealer_street || ""},${vehicle.sellingdealer_dealercountry || ""}-${vehicle.sellingdealer_postalcode || ""} ${vehicle.sellingdealer_city || ""}`}
              </div>

              <div className="heading-large mt-auto flex flex-row items-end gap-1 font-semibold lg:pt-4">
                {vehicle.customerprice
                  ? formatPrice(vehicle.customerprice)
                  : "—"}{" "}
                <span className="mb-1 text-xs font-normal">{mwstText}</span>
              </div>
            </div>
          </div>
        </div>
        {isMobile && (
          <div className="flex flex-col items-center gap-1 p-4 pt-4 text-xl leading-none font-semibold">
            <div className="flex w-full flex-col gap-2">
              <Button
                className={cn(
                  "text-accent-foreground",
                  isBookmarked && "bg-primary text-primary-foreground"
                )}
                variant={isBookmarked ? "default" : "secondary"}
                onClick={handleBookmarkToggle}
                aria-label={
                  isBookmarked ? "Fahrzeug entfernen" : "Fahrzeug merken"
                }
              >
                {isBookmarked ? "Entfernen" : "Fahrzeug merken"}
                <BookmarkIcon
                  size={20}
                  fill={isBookmarked ? "currentColor" : "none"}
                />
              </Button>
              <Button variant="default">
                <span>Details</span> <ArrowRightIcon size={20} />
              </Button>
            </div>
          </div>
        )}
      </div>
    </Link>
  );
}
